import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Sanitize HTML to prevent XSS attacks
const sanitizeHtml = (html: string): string => {
  // Remove script tags and their content
  let clean = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

  // Remove onclick, onerror, onload, etc. event handlers
  clean = clean.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
  clean = clean.replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '');

  // Remove javascript: URLs
  clean = clean.replace(/href\s*=\s*["']javascript:[^"']*["']/gi, 'href="#"');
  clean = clean.replace(/src\s*=\s*["']javascript:[^"']*["']/gi, 'src=""');

  // Remove data: URLs (can be used for XSS)
  clean = clean.replace(/src\s*=\s*["']data:[^"']*["']/gi, 'src=""');

  // Remove style tags (could contain expressions in older IE)
  clean = clean.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');

  // Remove iframe, object, embed tags
  clean = clean.replace(/<(iframe|object|embed|form|input|button)\b[^>]*>.*?<\/\1>/gi, '');
  clean = clean.replace(/<(iframe|object|embed|form|input|button)\b[^>]*\/?>/gi, '');

  return clean;
};

// Extract body content from full HTML document
const extractBodyContent = (html: string): string => {
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  return bodyMatch ? bodyMatch[1] : html;
};

const StaticTermsPage: React.FC = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const response = await fetch('/tc.html');
        if (!response.ok) {
          throw new Error('Failed to fetch terms');
        }
        const html = await response.text();
        const bodyContent = extractBodyContent(html);
        setContent(bodyContent);
      } catch (err) {
        console.error('Error fetching terms:', err);
        setError('Failed to load terms of service. Please refresh the page.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTerms();
  }, []);

  const handleGoBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#FFFCEF]">
        <div className="text-center">
          <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#2C2731] border-r-transparent" />
          <p className="text-sm text-[#2C2731]/70">Loading terms of service...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#FFFCEF]">
        <div className="max-w-md rounded-2xl border border-red-300 bg-red-50 p-6 text-center">
          <p className="mb-2 text-sm font-semibold text-red-900">Error Loading Terms</p>
          <p className="text-sm text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full items-stretch overflow-hidden">
      <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FFFCEF]">
        <header className="flex h-14 flex-shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
          <button
            onClick={handleGoBack}
            className="flex items-center gap-2 text-sm text-[#FFFCEF] hover:text-[#FFFCEF]/80 transition-colors"
          >
            <span className="text-lg leading-none">&larr;</span>
            <span>Back</span>
          </button>
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.webp" alt="Guides Collective" className="h-7 w-auto" />
          </div>
          <div className="w-20" aria-hidden />
        </header>

        <main className="flex flex-1 min-h-0 flex-col overflow-hidden px-6 pb-6 pt-6">
          <div className="mx-auto w-full max-w-4xl flex flex-col flex-1 min-h-0">
            <div className="flex flex-col gap-2 mb-6 flex-shrink-0">
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-[#214D3C]">Guides Collective</p>
              <h1 className="font-recoleta text-[32px] font-normal leading-[1.1] text-[#304031]">Terms and Conditions</h1>
              <p className="text-[14px] leading-relaxed text-[#214D3C]">
                Complete terms and conditions for your reference.
              </p>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto rounded-3xl border border-[#304031]/15 bg-white/70 p-6 shadow-inner">
              <div
                className="prose prose-sm max-w-none [&_a]:text-[#214D3C] [&_a]:underline [&_a]:cursor-pointer hover:[&_a]:text-[#304031] [&_p]:text-[#2C2731] [&_p]:leading-7 [&_p]:mb-4"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
              />
            </div>

            <div className="mt-4 flex justify-center flex-shrink-0">
              <button
                onClick={handleGoBack}
                className="inline-flex items-center gap-2 rounded-full border border-[#304031] bg-[#304031] px-6 py-3 text-sm font-medium text-[#FFFCEF] shadow-lg transition-all hover:bg-[#214D3C] hover:-translate-y-0.5"
              >
                <span className="text-lg leading-none">&larr;</span>
                <span>Back</span>
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default StaticTermsPage;
